import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Drop } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { fmtKg, fmtL, fmtNum, fmtDate } from "@/lib/api";
import { useT } from "@/contexts/I18nContext";
import { SystemBadge } from "@/components/Badges";

// ------------------------------------------------------------------
// Small reusable stat card (used in the cycle section)
// ------------------------------------------------------------------
export function StatCard({ label, value, hint, color = "#083D1E", testid }) {
    return (
        <Card className="p-5 card-paper shadow-none">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
            <div className="metric-number text-2xl mt-2" style={{ color }} data-testid={testid}>{value}</div>
            {hint ? <div className="text-xs text-muted-foreground mt-1">{hint}</div> : null}
        </Card>
    );
}

// ------------------------------------------------------------------
// Animal header: lot, production badges, system, name, metadata
// ------------------------------------------------------------------
export function AnimalHeader({ animal, isMilk }) {
    const { t } = useT();
    return (
        <header className="flex items-start justify-between flex-wrap gap-3">
            <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="secondary">{animal.lot}</Badge>
                    {isMilk ? (
                        <Badge className="bg-[#4A6E00]/15 text-[#4A6E00] border border-[#4A6E00]/30 hover:bg-[#4A6E00]/15">
                            <Drop size={12} className="mr-1" weight="fill" /> {t("production_milk")}
                        </Badge>
                    ) : (
                        <Badge className="bg-[#083D1E]/10 text-[#083D1E] border border-[#083D1E]/20 hover:bg-[#083D1E]/10">
                            {t("production_weight")}
                        </Badge>
                    )}
                    <SystemBadge system={animal.production_system || "feedlot"} />
                    <span className="text-sm text-muted-foreground">RFID: {animal.rfid}</span>
                </div>
                <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{animal.name}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {animal.breed || t("no_birth_date")} · {t("born")}: {animal.birth_date || "—"}
                    {animal.diet_type ? <> · {t("diet_type")}: {animal.diet_type}</> : null}
                </p>
            </div>
        </header>
    );
}

// ------------------------------------------------------------------
// Cycle stats grid (weight or milk animals)
// ------------------------------------------------------------------
export function AnimalCycleStats({ animal, data, isMilk }) {
    const { t } = useT();
    const gain = (data.last_weight || 0) - (animal.cycle_start_weight || 0);
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {isMilk ? (
                <>
                    <StatCard
                        label={t("cycle_start")}
                        value={animal.cycle_start_at ? fmtDate(animal.cycle_start_at) : t("no_cycle")}
                        testid="stat-cycle-start"
                    />
                    <StatCard
                        label={t("accumulated_milk")}
                        value={fmtL(data.total_milk_liters)}
                        color="#4A6E00"
                        testid="stat-accumulated-milk"
                    />
                </>
            ) : (
                <>
                    <StatCard
                        label={t("cycle_start_weight")}
                        value={fmtKg(animal.cycle_start_weight)}
                        hint={animal.cycle_start_at ? fmtDate(animal.cycle_start_at) : t("no_cycle")}
                        testid="stat-cycle-start"
                    />
                    <StatCard
                        label={t("last_weight")}
                        value={fmtKg(data.last_weight)}
                        hint={data.last_weight_at ? fmtDate(data.last_weight_at) : t("no_weighings_yet")}
                        testid="stat-last-weight"
                    />
                    <StatCard
                        label={t("provisional_gain")}
                        value={fmtKg(gain)}
                        color={gain > 0 ? "#083D1E" : "#5C6B61"}
                        testid="stat-gain"
                    />
                </>
            )}
            <StatCard
                label={t("consumption_as_fed")}
                value={fmtKg(data.total_feed_as_fed)}
                hint={t("sum_as_fed")}
                testid="stat-consumption-as-fed"
            />
            <StatCard
                label={t("consumption_dry_matter")}
                value={fmtKg(data.total_feed_dry_matter)}
                color="#4A6E00"
                hint={t("sum_dry_matter")}
                testid="stat-consumption-dm"
            />
        </div>
    );
}

// ------------------------------------------------------------------
// Reports table for an animal
// ------------------------------------------------------------------
export function AnimalReportsTable({ reports }) {
    const { t } = useT();
    return (
        <Card className="card-paper shadow-none overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{t("date")}</TableHead>
                        <TableHead>{t("production_system")}</TableHead>
                        <TableHead className="text-right">{t("production_label")}</TableHead>
                        <TableHead className="text-right">{t("total_feed_period")}</TableHead>
                        <TableHead className="text-right">{t("animal_weighing")}</TableHead>
                        <TableHead className="text-right">{t("conversion_rate")}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {reports.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                {t("no_reports_yet")}
                            </TableCell>
                        </TableRow>
                    ) : reports.map((r) => <ReportRow key={r.id} r={r} />)}
                </TableBody>
            </Table>
        </Card>
    );
}

function ReportRow({ r }) {
    const { t } = useT();
    return (
        <TableRow className="hover:bg-[#EAF3DA]/40">
            <TableCell className="text-xs">
                <div>{fmtDate(r.period_end)}</div>
                {r.duration_days != null ? (
                    <div className="text-muted-foreground">{r.duration_days} {t("days")}</div>
                ) : null}
            </TableCell>
            <TableCell>
                <SystemBadge system={r.production_system || "feedlot"} />
                {r.diet_type ? (
                    <div className="text-[10px] text-muted-foreground mt-1 max-w-[140px] truncate" title={r.diet_type}>
                        {r.diet_type}
                    </div>
                ) : null}
            </TableCell>
            <TableCell className="text-right">
                {r.production_unit === "L" ? (
                    <div className="flex items-center justify-end gap-1.5">
                        <Drop size={13} color="#4A6E00" weight="fill" />
                        <span className="metric-number text-[#4A6E00]">{fmtL(r.production_amount)}</span>
                    </div>
                ) : (
                    <span className="metric-number text-[#083D1E]">{fmtKg(r.production_amount)}</span>
                )}
            </TableCell>
            <TableCell className="text-right">
                <div className="metric-number">{fmtKg(r.total_feed_as_fed)}</div>
                <div className="text-[10px] text-muted-foreground">MS: {fmtKg(r.total_feed_dry_matter)}</div>
            </TableCell>
            <TableCell className="text-right metric-number text-muted-foreground">
                {r.production_type === "milk" ? "—" : fmtKg(r.end_weight)}
            </TableCell>
            <TableCell className="text-right">
                <div className="metric-number text-[#083D1E] font-semibold">{fmtNum(r.fcr_as_fed)}</div>
                <div className="text-[10px] text-[#4A6E00] font-medium">MS: {fmtNum(r.fcr_dry_matter)}</div>
            </TableCell>
        </TableRow>
    );
}

// ------------------------------------------------------------------
// Weighings / Milk / Feed events tables (simple two/three-column tables)
// ------------------------------------------------------------------
export function AnimalWeighingsTable({ weighings }) {
    const { t } = useT();
    return (
        <Card className="card-paper shadow-none overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{t("date")}</TableHead>
                        <TableHead className="text-right">{t("weight")}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {weighings.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={2} className="text-center py-8 text-muted-foreground">
                                {t("no_weighings_yet")}
                            </TableCell>
                        </TableRow>
                    ) : weighings.map((w) => (
                        <TableRow key={w.id}>
                            <TableCell>{fmtDate(w.timestamp)}</TableCell>
                            <TableCell className="text-right metric-number">{fmtKg(w.weight_kg)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}

export function AnimalMilkTable({ milk }) {
    const { t } = useT();
    return (
        <Card className="card-paper shadow-none overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{t("date")}</TableHead>
                        <TableHead>{t("origin")}</TableHead>
                        <TableHead className="text-right">{t("liters")}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {milk.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                                {t("no_milk")}
                            </TableCell>
                        </TableRow>
                    ) : milk.map((m) => (
                        <TableRow key={m.id}>
                            <TableCell>{fmtDate(m.timestamp)}</TableCell>
                            <TableCell>
                                <Badge
                                    variant={m.source === "parlour" ? "default" : "secondary"}
                                    className={m.source === "parlour" ? "bg-[#083D1E] text-white hover:bg-[#083D1E]" : ""}
                                >
                                    {m.source === "parlour" ? t("origin_parlour") : t("origin_manual")}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-right metric-number">{fmtL(m.liters)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}

export function AnimalFeedEventsTable({ events }) {
    const { t } = useT();
    return (
        <Card className="card-paper shadow-none overflow-hidden">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{t("date")}</TableHead>
                        <TableHead>{t("origin")}</TableHead>
                        <TableHead className="text-right">{t("feed_as_fed")}</TableHead>
                        <TableHead className="text-right">{t("feed_dry_matter")}</TableHead>
                        <TableHead className="text-right">{t("dm_pct")}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {events.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                {t("no_consumption_yet")}
                            </TableCell>
                        </TableRow>
                    ) : events.map((e) => (
                        <TableRow key={e.id}>
                            <TableCell>{fmtDate(e.timestamp)}</TableCell>
                            <TableCell>
                                <Badge
                                    variant={e.source === "feeder" ? "default" : "secondary"}
                                    className={e.source === "feeder" ? "bg-[#083D1E] text-white hover:bg-[#083D1E]" : ""}
                                >
                                    {e.source === "feeder" ? t("origin_feeder") : t("origin_manual")}
                                </Badge>
                            </TableCell>
                            <TableCell className="text-right metric-number">{fmtKg(e.feed_kg)}</TableCell>
                            <TableCell className="text-right metric-number">{fmtKg(e.feed_dry_matter_kg)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">
                                {fmtNum(e.dry_matter_fraction_used * 100, 1)}%
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}
